import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Dates travel as 'YYYY-MM-DD' strings. Inside the picker they are whole UTC
// days counted from the epoch, so arithmetic never meets a DST shift and
// "today" is the UTC day the dashboard chart (times in UTC) calls today.
const DAY_MS = 86400000;
const toDay = (iso) => (iso ? Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY_MS) : null);
const toIso = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const dayOf = (y, m, d) => Math.floor(Date.UTC(y, m, d) / DAY_MS);
const parts = (day) => {
  const d = new Date(day * DAY_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
};
const todayDay = () => Math.floor(Date.now() / DAY_MS);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const fmt = (day, withYear) => new Date(day * DAY_MS).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
});

// "Oct 12 – Oct 18, 2026"; the year shows once when both ends share it.
const formatRange = (start, end) => {
  if (start == null || end == null) return 'Select range';
  if (start === end) return fmt(end, true);
  if (parts(start).y === parts(end).y) return `${fmt(start)} – ${fmt(end, true)}`;
  return `${fmt(start, true)} – ${fmt(end, true)}`;
};

// Compact hint next to a preset: "17–19 Oct", "18 Sep–17 Oct".
const shortRange = (start, end) => {
  const a = parts(start);
  const b = parts(end);
  const mon = (p) => MONTHS[p.m].slice(0, 3);
  if (a.m === b.m && a.y === b.y) return `${a.d}–${b.d} ${mon(b)}`;
  return `${a.d} ${mon(a)}–${b.d} ${mon(b)}`;
};

const presetRanges = (today) => {
  const { y, m } = parts(today);
  return [
    ['Next 48 hours', today, today + 2],
    ['Last 10 days', today - 10, today + 1],
    ['Next 10 days', today, today + 10],
    ['Last 30 days', today - 29, today],
    ['This month', dayOf(y, m, 1), dayOf(y, m + 1, 0)],
  ];
};

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

// One month, Monday first. The range is one continuous band with solid caps
// on its ends; days after today carry the chart's future tint, so the grid
// reads like the chart's time axis.
const MonthGrid = ({ view, start, end, today, onPick, onHover, onShift }) => {
  const first = dayOf(view.y, view.m, 1);
  const gridStart = first - ((new Date(first * DAY_MS).getUTCDay() + 6) % 7);

  return (
    <div className="px-3.5 pt-3 pb-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <button type="button" onClick={() => onShift(-1)} aria-label="Previous month" className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 ${focusRing}`}>
          <ChevronLeftIcon aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
        <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{MONTHS[view.m]} {view.y}</span>
        <button type="button" onClick={() => onShift(1)} aria-label="Next month" className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 ${focusRing}`}>
          <ChevronRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-[repeat(7,2.25rem)] gap-y-0.5" onMouseLeave={() => onHover(null)}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="flex h-6 items-center justify-center text-[11px] text-gray-400 dark:text-gray-500">{w}</div>
        ))}
        {Array.from({ length: 42 }, (_, i) => {
          const day = gridStart + i;
          const col = i % 7;
          const inMonth = parts(day).m === view.m;
          const isCap = start != null && (day === start || day === end);
          const inBand = start != null && end != null && day > start && day < end;
          const single = start === end;

          // One background per cell: band, half band behind a cap, or tint.
          let bg = '';
          if (inBand) {
            bg = `bg-brand-50 dark:bg-brand-900/40 ${col === 0 ? 'rounded-l-full' : ''} ${col === 6 ? 'rounded-r-full' : ''}`;
          } else if (isCap && !single && day === start && col !== 6) {
            bg = 'bg-gradient-to-r from-transparent from-50% to-brand-50 to-50% dark:to-brand-900/40';
          } else if (isCap && !single && day === end && col !== 0) {
            bg = 'bg-gradient-to-l from-transparent from-50% to-brand-50 to-50% dark:to-brand-900/40';
          } else if (day > today) {
            bg = 'bg-slate-900/[0.035] dark:bg-gray-100/[0.04]';
          }

          let num = 'text-gray-700 group-hover:bg-gray-100 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:bg-gray-700 dark:group-hover:text-gray-100';
          if (isCap) num = 'bg-brand-600 font-semibold text-white dark:bg-brand-500';
          else if (inBand) num = 'text-brand-700 group-hover:bg-brand-100 dark:text-brand-300 dark:group-hover:bg-brand-900/60';
          else if (!inMonth) num = 'text-gray-400 opacity-60 group-hover:bg-gray-100 dark:text-gray-500 dark:group-hover:bg-gray-700';
          const todayMark = day === today && !isCap ? 'outline-dashed outline-1 -outline-offset-1 outline-gray-900/40 dark:outline-gray-100/45' : '';

          return (
            <button
              key={day}
              type="button"
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover(day)}
              aria-label={fmt(day, true)}
              aria-pressed={isCap || inBand}
              className={`group flex h-8 items-center justify-center ${bg} ${focusRing}`}
            >
              <span className={`inline-flex h-[30px] w-[30px] items-center justify-center rounded-full font-mono text-xs tabular-nums ${num} ${todayMark}`}>
                {parts(day).d}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onDateRangeChange,
  // 'chip' renders a compact pill trigger (for the dashboard chip bar) and a
  // wide popover with the presets beside the month; anything else keeps the
  // full-width button and a narrow popover with the presets above it.
  variant = 'default',
  // Optional controlled-open mode: when `open` is provided the parent owns
  // the open state (used to coordinate with sibling popovers). Otherwise the
  // component manages its own state as before.
  open,
  onOpenChange,
}) {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = (next) => {
    const value = typeof next === 'function' ? next(isOpen) : next;
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };
  const isChip = variant === 'chip';
  const today = todayDay();
  const start = toDay(startDate);
  const end = toDay(endDate);

  // Draft range; Apply commits it. `picking` means the first day is set and
  // the next click sets the other end (hover previews the band meanwhile).
  const [draft, setDraft] = useState({ start, end });
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState(null);
  const [view, setView] = useState(() => parts(end ?? today));
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside. Skipped in controlled mode, where
  // the parent handles outside clicks for the whole popover group.
  useEffect(() => {
    if (isControlled) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setInternalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isControlled]);

  // Every opening (and every outside change) starts from the applied range,
  // showing the month of its last day.
  useEffect(() => {
    if (!isOpen) return;
    setDraft({ start: toDay(startDate), end: toDay(endDate) });
    setPicking(false);
    setHover(null);
    setView(parts(toDay(endDate) ?? todayDay()));
  }, [isOpen, startDate, endDate]);

  const pick = (day) => {
    if (!picking) {
      setDraft({ start: day, end: day });
      setPicking(true);
    } else {
      setDraft((d) => ({ start: Math.min(d.start, day), end: Math.max(d.start, day) }));
      setPicking(false);
    }
    setHover(null);
  };

  const applyPreset = (s, e) => {
    setDraft({ start: s, end: e });
    setPicking(false);
    setView(parts(e));
  };

  const handleApply = () => {
    const s = toIso(draft.start);
    const e = toIso(draft.end);
    // Prefer combined handler to avoid race conditions with separate dispatches
    if (onDateRangeChange) {
      onDateRangeChange(s, e);
    } else {
      if (onStartDateChange) onStartDateChange(s);
      if (onEndDateChange) onEndDateChange(e);
    }
    setOpen(false);
  };

  // While picking, the band runs from the first day to the hovered one.
  let shownStart = draft.start;
  let shownEnd = draft.end;
  if (picking && hover != null) {
    shownStart = Math.min(draft.start, hover);
    shownEnd = Math.max(draft.start, hover);
  }

  const hasDraft = draft.start != null && draft.end != null;
  const changed = draft.start !== start || draft.end !== end;
  const presets = presetRanges(today);
  const isActive = (s, e) => !picking && draft.start === s && draft.end === e;
  const dayCount = hasDraft ? draft.end - draft.start + 1 : 0;

  const grid = (
    <MonthGrid
      view={view}
      start={shownStart}
      end={shownEnd}
      today={today}
      onPick={pick}
      onHover={(day) => picking && setHover(day)}
      onShift={(delta) => setView((v) => parts(dayOf(v.y, v.m + delta, 1)))}
    />
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      {isChip ? (
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          aria-expanded={isOpen}
          className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-medium transition-colors ${
            isOpen
              ? 'border-brand-300 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:border-brand-700 dark:text-brand-300'
              : 'border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 hover:border-brand-300 hover:bg-brand-50 dark:hover:bg-gray-700'
          }`}
        >
          <CalendarIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          <span className="font-semibold text-gray-900 dark:text-gray-100">{formatRange(start, end)}</span>
          <ChevronDownIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          aria-expanded={isOpen}
          className="w-full flex items-center justify-between px-3 h-[42px] text-sm border border-gray-200/70 dark:border-gray-600/70 rounded-md bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 shadow-sm"
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span>{formatRange(start, end)}</span>
          </span>
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Choose dates"
          className={`absolute z-50 mt-2 bg-white dark:bg-gray-870 rounded-lg shadow-lg border border-gray-200/70 dark:border-gray-600/70 ${isChip ? 'right-0 w-[31.25rem]' : 'left-0 w-[19.5rem]'}`}
        >
          {isChip ? (
            <div className="grid grid-cols-[13.75rem_1fr]">
              <div className="flex flex-col gap-0.5 border-r border-gray-200/70 p-2 dark:border-gray-600/70">
                {presets.map(([label, s, e]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => applyPreset(s, e)}
                    className={`flex items-baseline justify-between gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-[13px] ${focusRing} ${
                      isActive(s, e)
                        ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                    }`}
                  >
                    <span>{label}</span>
                    <span className={`font-mono text-[11px] font-normal ${isActive(s, e) ? 'opacity-80' : 'text-gray-400 dark:text-gray-500'}`}>
                      {shortRange(s, e)}
                    </span>
                  </button>
                ))}
              </div>
              {grid}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5 border-b border-gray-200/70 p-3 dark:border-gray-600/70">
                {presets.map(([label, s, e]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => applyPreset(s, e)}
                    className={`h-7 rounded-md border px-2.5 text-xs font-medium ${focusRing} ${
                      isActive(s, e)
                        ? 'border-brand-300 bg-brand-50 font-semibold text-brand-700 dark:border-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                        : 'border-gray-200/70 text-gray-700 hover:bg-gray-100 dark:border-gray-600/70 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex justify-center">{grid}</div>
            </>
          )}

          {/* The narrow popover gives the readout its own line. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-gray-200/70 px-3 py-2.5 dark:border-gray-600/70">
            <span className={`mr-auto min-w-0 truncate text-xs text-gray-500 dark:text-gray-400 ${isChip ? '' : 'w-full'}`} aria-live="polite">
              {picking && 'Pick the last day'}
              {!picking && hasDraft && (
                <>
                  <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">{dayCount}</span>
                  {dayCount === 1 ? ' day' : ' days'} ·{' '}
                  <span className="font-mono text-gray-900 dark:text-gray-100">{formatRange(draft.start, draft.end)}</span>
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`${isChip ? '' : 'ml-auto'} h-[30px] rounded-md px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 ${focusRing}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!changed || picking || !hasDraft}
              className={`h-[30px] rounded-md bg-brand-600 px-3 text-[13px] font-medium text-white hover:bg-brand-700 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-600 dark:disabled:hover:bg-brand-500 ${focusRing}`}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
